import type { WebViewDto } from '@/api/webs'
import type { WebinarRoomType } from '@/types/webinarRoom'

type OnlineEvent = NonNullable<WebViewDto['OnlineEvent']>

/** `WebinarRoomType`'s descriptions, as `OnlineEventViewDto.Room` carries them. */
export const BIGMARKER_ROOM: WebinarRoomType = 'BigMarker'
export const ORBITALX_ROOM: WebinarRoomType = 'OrbitalX room'

/** Whether a mode, as the wire carries it (`OnlineModeType`'s description), is an On-demand recording. */
export function isOnDemandMode(modeType: string | null | undefined): boolean {
    return modeType?.trim().toLowerCase() === 'on-demand (recording only)'
}

/** Whether a mode is Hybrid, the one that gives an OrbitalX-room web a replay (ORB-1086). */
export function isHybridMode(modeType: string | null | undefined): boolean {
    return modeType?.trim().toLowerCase() === 'hybrid (live then on-demand)'
}

/**
 * Whether the web runs in our own room rather than BigMarker (ORB-1086). Keyed on the room field alone:
 * a room web gets a ConferenceId at sign-off, so a conference is no sign of BigMarker any more, and an
 * older imported BigMarker web can have no channel. A missing value is BigMarker, as the API reads it.
 */
export function isOrbitalXRoom(event: Pick<OnlineEvent, 'Room'> | null | undefined): boolean {
    return event?.Room === ORBITALX_ROOM
}

/** An On-demand recording with neither a channel nor a webinar has opted out of BigMarker (ORB-1076). */
function optedOutOfBigMarker(event: Pick<OnlineEvent, 'ModeType' | 'ChannelId' | 'ConferenceId'> | null | undefined): boolean {
    return isOnDemandMode(event?.ModeType) && !event?.ChannelId?.trim() && !event?.ConferenceId?.trim()
}

/**
 * Mirror of the API's sign-off rule: an OrbitalX-room web signs off without BigMarker, and so does an
 * On-demand recording that opted out of it (ORB-1076). Anything else signs off through BigMarker as before.
 */
export function signOffSkipsBigMarker(event: Pick<OnlineEvent, 'Room' | 'ModeType' | 'ChannelId' | 'ConferenceId'> | null | undefined): boolean {
    return isOrbitalXRoom(event) || optedOutOfBigMarker(event)
}

/**
 * Mirror of the API's activation rule (`ValidateWebActivationTransition`): an online event has no
 * BigMarker conference until sign-off creates one, so Marketing is unreachable before then.
 *
 * Mirrored here rather than left to the server because this form saves as one all-or-nothing PATCH —
 * the API validates the status transition and throws before applying any field, so a rejected status
 * discarded every other edit in the same save, including a slug the user had just typed. The form kept
 * rendering the typed slug, so nothing signalled the loss and the next symptom was Deploy failing on a
 * missing slug (ORB-478).
 *
 * Keyed on the conference, not on sign-off: a web imported from BigMarker carries a ConferenceId from
 * creation and must stay free to reach Marketing without being signed off. Scoped to a status that is
 * actually changing, matching the server, so a web already in Marketing whose conference later went
 * missing is never left unable to select its own current value. An On-demand recording with no channel is
 * exempt, as on the server: it has opted out of BigMarker, so it will never have a conference (ORB-1076).
 * An OrbitalX-room web is not: sign-off sets its ConferenceId to the room's id, and the server waits for
 * that just as it waits for a BigMarker webinar (ORB-1086).
 *
 * Trimmed because the server tests presence with `IsNullOrWhiteSpace` while JS reads a whitespace
 * string as truthy. Untrimmed, a document carrying `" "` would let this guard wave Marketing through
 * and the API would still reject the PATCH, re-opening the very data-loss path this closes.
 * `OverviewCard` already trims for the same reason.
 *
 * `ConferenceNotFound` is deliberately NOT consulted. The server rule does not look at it, so
 * blocking on it here would refuse a transition the API accepts — divergence in the other direction,
 * and a mirror stricter than the thing it mirrors is its own bug.
 */
export function marketingNeedsConference(web: Pick<WebViewDto, 'Type' | 'Status' | 'OnlineEvent'>): boolean {
    return web.Type === 'Online Event'
        && !optedOutOfBigMarker(web.OnlineEvent)
        && !web.OnlineEvent?.ConferenceId?.trim()
        && web.Status !== 'Marketing'
}
